// src/i18n/en/views/dominioPropio.js
//
// Guía que ve el anfitrión para apuntar su dominio a la web del constructor.
// Base en inglés del fichero; el original con las notas largas está en es/.
//
// Marcadores que rellena la pantalla:
//   {dominio} {destino} {nombre} {tipo} {valor} {encontrado} {actual} {sitio}
//   {panel} {panelCorto}

export const dominioPropio = {
  cabecera: {
    titulo: "Eigene Domain verbinden",
    subtitulo:
      "Ihre Website liegt derzeit unter {actual}. Wenn Sie fertig sind, liegt sie unter Ihrer gewohnten Adresse.",
    duracion: "Zehn Minuten Ihrer Zeit. Den Rest erledigt das Internet von allein.",
  },

  pasos: {
    uno: "Ihre Domain",
    dos: "Die beiden Einträge in {panelCorto}",
    tres: "Prüfen",
    unoDetalle: "Sie geben die Adresse ein, die Sie nutzen möchten",
    dosDetalle: "Sie kopieren zwei Zeilen in Ihr Domain-Panel",
    tresDetalle: "Wir prüfen, ob alles stimmt",
    indicador: "Schritt {paso} von 3",
  },

  // ── Paso 1: escribir el dominio ────────────────────────────────────────────
  paso1: {
    titulo: "Welche Adresse möchten Sie für Ihre Website?",
    descripcion:
      "Geben Sie die Domain ein, die Ihnen bereits gehört. Wir verkaufen keine Domains: Hier verbinden Sie eine, die schon Ihre ist.",
    etiqueta: "Ihre Domain",
    marcador: "meinedomain.de",
    ayuda: "Schreiben Sie sie so, wie sie ist, ohne „https://“ und ohne Schrägstrich am Ende.",

    formaTitulo: "Und wie soll sie lauten?",
    formaRaiz: {
      titulo: "meinedomain.de",
      descripcion: "Die kurze Adresse, ohne Zusatz davor. Das ist die übliche Variante.",
    },
    formaWww: {
      titulo: "www.meinedomain.de",
      descripcion: "Die klassische Variante, mit „www“ davor.",
    },
    formaSubdominio: {
      titulo: "buchungen.meinedomain.de",
      descripcion:
        "Eine eigene Adresse, damit Sie die Website, die Sie unter {dominio} bereits haben, nicht anfassen müssen.",
    },
    subdominioEtiqueta: "Das Wort, das davor steht",
    subdominioMarcador: "buchungen",
    subdominioAyuda: "Nur das Wort. Den Rest ergänzen wir.",

    avisoWebExistente: {
      titulo: "Haben Sie unter dieser Adresse schon eine Website?",
      texto:
        "Wenn {dominio} heute eine andere Website zeigt, wird sie nicht mehr angezeigt und diese hier nimmt ihren Platz ein. Wenn Sie sie lieber behalten möchten, nutzen Sie eine eigene Adresse wie buchungen.{dominio}.",
    },

    boton: "Weiter",
    botonComprobando: "Domain wird geprüft…",
  },

  // ── Dónde tiene el dominio ─────────────────────────────────────────────────
  proveedor: {
    pregunta: "Wo haben Sie {dominio} gekauft oder verwalten sie?",
    ayuda:
      "Das ist das Unternehmen, an das Sie jedes Jahr die Verlängerung zahlen. Falls Sie sich nicht erinnern, suchen Sie in Ihren E-Mails nach der letzten Rechnung für die Domain.",
    cloudflare: "Cloudflare",
    cloudflareDetalle: "Vollständige Anleitung, Bildschirm für Bildschirm",
    otro: "Ein anderes Unternehmen",
    otroDetalle: "GoDaddy, IONOS, Namecheap, Dinahosting…",
    otroTexto:
      "Die Einträge sind genau dieselben. Nur die Position der Schaltfläche ändert sich. Suchen Sie in Ihrem Panel nach etwas mit der Bezeichnung „DNS“, „DNS-Zone“ oder „DNS-Verwaltung“ und kopieren Sie die Zeilen aus der Tabelle oben dort hinein, eine nach der anderen.",
    noLoSe: "Ich weiß es nicht",
    noLoSeTexto:
      "Schreiben Sie uns mit Ihrer Domain und wir sagen Ihnen, wo sie liegt und was geändert werden muss.",
  },

  // ── Paso 2: los registros ──────────────────────────────────────────────────
  paso2: {
    titulo: "Jetzt müssen Sie Ihrer Domain sagen, wo Ihre Website liegt",
    descripcion:
      "Ihre Domain ist ein Name. Für sich allein führt sie nirgendwohin: Jemand muss in ein Notizbuch schreiben, dass dieser Name zu unserem Server gehört. Dieses Notizbuch heißt DNS und liegt in Ihrem Domain-Panel.",

    queEsUnRegistro: {
      titulo: "Was Sie genau tun werden",
      texto:
        "Sie fügen diesem Notizbuch eine Zeile hinzu. Nur eine. Sie hat drei Felder: wie sie heißt, wohin sie zeigt und ob sie durch einen Filter läuft. Sonst nichts.",
      textoOtro:
        "Sie fügen diesem Notizbuch Zeilen hinzu. Jede hat zwei Felder: wie sie heißt und wohin sie zeigt. Sonst nichts.",
    },

    copiaTitulo: "Kopieren Sie diese Daten genau so, wie sie sind",
    copiaAyuda:
      "Nutzen Sie die Kopieren-Schaltflächen. Ein Leerzeichen zu viel oder ein falscher Buchstabe und es funktioniert nicht.",

    tabla: {
      tipo: "Typ",
      nombre: "Name",
      destino: "Ziel",
      contenido: "Inhalt",
      proxy: "Proxy-Status",
      ttl: "TTL",
      valorProxy: "DNS only (graue Wolke)",
      valorTtl: "Auto",
    },

    copiar: "Kopieren",
    copiado: "Kopiert",
    copiarTodo: "Daten kopieren",

    tipos: {
      cnameTitulo: "CNAME",
      cnameTexto:
        "Das ist eine Weiterleitung: Sie besagt, dass Ihre Domain unter {destino} ausgeliefert wird. Sollten wir je den Server wechseln, müssen Sie nichts anfassen.",
      txtTitulo: "TXT",
      txtTexto:
        "Das ist ein signierter Vermerk. Er führt nirgendwohin: Er belegt nur, dass die Domain Ihnen gehört. Sie können ihn löschen, sobald die Website funktioniert, aber es ist einfacher, ihn stehen zu lassen.",
    },

    nombreTitulo: "Was Sie in „Name“ schreiben",
    nombreRaiz:
      "Für die kurze Domain ({dominio}) schreiben Sie ein At-Zeichen: @. Im DNS-Notizbuch bedeutet das At-Zeichen „die nackte Domain, ohne Zusatz davor“. Schreiben Sie nicht die ganze {dominio}.",
    nombreWww: "Für www.{dominio} schreiben Sie nur: www",
    nombreSubdominio:
      "Für {nombre}.{dominio} schreiben Sie nur das Wort davor: {nombre}",
    nombreAviso:
      "In Cloudflare schreiben Sie nur diesen Teil. Cloudflare ergänzt „.{dominio}“ von allein und zeigt es Ihnen unter dem Feld an.",
    nombreAvisoOtro:
      "In fast jedem Panel schreiben Sie nur diesen Teil und „.{dominio}“ wird ergänzt. Falls Ihres den ganzen Namen verlangt, schreiben Sie {nombre}.{dominio}.",
    nombreAvisoOtroRaiz:
      "Wenn Ihr Panel „@“ nicht akzeptiert, lassen Sie den Namen leer: In vielen bedeutet das dasselbe, die Domain ohne Zusatz davor.",

    volver: "Erledigt, zurück zu WACheckin",
    ayudaEnviar: "Diese Daten per E-Mail senden",
    ayudaEnviarTexto:
      "Wenn Ihre IT-Person oder wer auch immer Ihre Website erstellt hat es übernimmt, senden wir die Anleitung an Ihre E-Mail-Adresse, damit Sie sie weiterleiten können.",
  },

  // ── Cloudflare, paso a paso ────────────────────────────────────────────────
  cloudflare: {
    titulo: "So geht es in Cloudflare",
    subtitulo: "Zehn Minuten. Halten Sie E-Mail und Passwort für Cloudflare bereit.",

    pasos: {
      uno: "Melden Sie sich unter dash.cloudflare.com mit Ihrem Konto an.",
      dos: "Der erste Bildschirm listet Ihre Domains auf. Klicken Sie auf {dominio}.",
      tres: "Öffnen Sie im linken Menü „DNS“ und dann „Records“.",
      cuatro:
        "Sie sehen eine Liste mit den Zeilen, die Sie bereits haben. Klicken Sie auf die Schaltfläche „Add record“.",
      cinco: "Wählen Sie unter „Type“ im Aufklappmenü {tipo}.",
      seis: "Schreiben Sie unter „Name“ {nombre}",
      siete: "Fügen Sie unter „Target“ {valor} ein",
      ocho:
        "Klicken Sie unter „Proxy status“ auf die orange Wolke. Sie muss grau werden und „DNS only“ anzeigen.",
      nueve: "Lassen Sie „TTL“ auf „Auto“. Nicht anfassen.",
      diez: "Klicken Sie auf „Save“.",
      once:
        "Wenn die Tabelle oben mehr als eine Zeile hat, wiederholen Sie ab Schritt 4 mit der nächsten.",
    },

    hecho: "Das war es. Gehen Sie zurück zu WACheckin und klicken Sie auf „Prüfen“.",

    avisoAntes: {
      titulo: "Lesen Sie das, bevor Sie speichern",
      texto:
        "Cloudflare färbt diese Wolke von allein orange, ohne zu fragen. Wenn Sie sie orange lassen, funktioniert die Verbindung nicht. Schritt 8 ist der wichtigste auf diesem ganzen Bildschirm.",
    },

    accesoNegado: {
      titulo: "Kein Zugang zu Cloudflare?",
      texto:
        "Das Konto läuft meist auf die Person, die Ihre Website erstellt hat. Bitten Sie sie, diese Schritte auszuführen oder Sie als Mitglied der Domain hinzuzufügen.",
    },
  },

  // ── La nube naranja ────────────────────────────────────────────────────────
  proxy: {
    titulo: "Die orange Wolke: daran scheitert es immer",
    queEs:
      "Neben jeder DNS-Zeile zeigt Cloudflare eine kleine Wolke. Orange bedeutet, dass der Verkehr zuerst über Cloudflare läuft. Grau bedeutet, dass er direkt durchgeht.",
    porQueFalla:
      "Bei oranger Wolke antwortet Cloudflare auf eine Anfrage zu Ihrer Domain nicht mit {destino}, sondern mit eigenen Adressen. Unsere Prüfung fragt an, bekommt Cloudflare und findet den gesuchten Eintrag nicht. Selbst wenn Sie ihn richtig geschrieben haben, ist er für uns, als gäbe es ihn nicht.",
    porQueFalla2:
      "Und es gibt eine zweite, schlimmere Folge: Das Sicherheitsschloss für Ihre Website wird ausgestellt, indem geprüft wird, ob die Domain bei uns ankommt. Bei oranger Wolke tut sie das nicht, also kann es nicht ausgestellt werden und der Browser warnt, dass die Website nicht sicher ist.",
    comoSeArregla:
      "Klicken Sie auf die Wolke. Sie wird grau und der Text daneben ändert sich zu „DNS only“. Speichern. Das ist alles.",
    grisEsSeguro:
      "Wenn Sie sie grau machen, bleibt Ihre Website nicht ungeschützt. Wir liefern Ihre Website selbst aus, mit ihrem Zertifikat und hinter unserem eigenen Schutz. Cloudflare war nur deshalb dazwischen, weil es standardmäßig so eingerichtet ist.",
    ttlBloqueado:
      "Ein Detail, das viele verwirrt: Solange die Wolke orange ist, lässt Cloudflare Sie die TTL nicht ändern. Sobald sie grau ist, wird sie freigegeben. Lassen Sie sie auf „Auto“.",
    etiquetaProxied: "Proxied · orange Wolke · NEIN",
    etiquetaSoloDns: "DNS only · graue Wolke · JA",
  },

  // ── El dominio corto (raíz) ────────────────────────────────────────────────
  raiz: {
    titulo: "Wenn Sie die kurze Domain nutzen: Es gibt nichts Besonderes zu tun",
    texto:
      "Es gibt eine alte Internetregel, nach der die kurze Domain ({dominio}, ohne Zusatz davor) diese Art von Eintrag nicht akzeptiert. Bei den meisten Unternehmen zwingt Sie das zu Umwegen.",
    flattening:
      "Cloudflare löst das von allein. Wenn der „Name“ @ ist, wendet Cloudflare etwas Eigenes namens CNAME flattening an: Es speichert Ihre Weiterleitung und antwortet auf Anfragen direkt mit der endgültigen Adresse. Sie müssen nichts aktivieren, es ist in jedem Tarif enthalten, auch im kostenlosen.",
    avisoAjusteGlobal:
      "Worauf Sie achten sollten: Fassen Sie die Option „CNAME flattening for all CNAME records“ unter „DNS“ → „Settings“ nicht an. Auf alles angewendet, werden Weiterleitungen nicht mehr so beantwortet, wie sie geschrieben sind, und die Eigentumsprüfungen anderer Dienste, die Sie vielleicht haben, gehen kaputt: E-Mail, Google, Ihr Shop …",
    tituloOtro: "Wenn Sie die kurze Domain nutzen ({dominio})",
    textoOtroA:
      "Die Domain ohne Zusatz davor nimmt Zeilen vom Typ A an, genau die aus der Tabelle. Jedes Unternehmen akzeptiert sie.",
    textoOtroCname:
      "Viele Unternehmen erlauben kein CNAME auf der Domain ohne Zusatz davor. Wenn Ihres diese Zeile nicht speichern lässt, nutzen Sie „www“ oder eine eigene Adresse wie buchungen.{dominio}, oder schreiben Sie uns und wir lösen es gemeinsam.",
    conflicto: {
      titulo: "Wenn auf der kurzen Domain schon etwas lag",
      texto:
        "Ein Name kann nicht zwei Ziele gleichzeitig haben. Wenn die Liste bereits eine Zeile vom Typ A oder CNAME mit dem „Namen“ @ enthält (oder „www“, falls Sie www nutzen möchten), löschen Sie sie, bevor Sie unsere hinzufügen. Cloudflare warnt Sie, wenn Sie es nicht tun.",
      textoOtro:
        "Ein Name kann nicht zwei Ziele gleichzeitig haben. Wenn Ihr Panel bereits eine Zeile vom Typ A oder CNAME mit demselben Namen enthält, löschen Sie sie, bevor Sie unsere hinzufügen.",
      comoBorrar:
        "Fahren Sie mit der Maus über diese Zeile, klicken Sie auf „Edit“ und dann auf „Delete“.",
      apunta:
        "Notieren Sie sich auf Papier, was dort stand, bevor Sie löschen, falls Sie zurück müssen.",
    },
  },

  // ── Cuánto tarda ───────────────────────────────────────────────────────────
  propagacion: {
    titulo: "Wie lange dauert es, bis es sichtbar ist?",
    texto:
      "Cloudflare veröffentlicht die Änderung sofort. Zeit braucht nur der Rest des Internets, um davon zu erfahren: Jeder Rechner behält eine Kopie des Notizbuchs eine Weile, um nicht tausendmal nachfragen zu müssen.",
    plazoNormal:
      "Mit TTL auf „Auto“ bittet Cloudflare darum, dass diese Kopie fünf Minuten gilt. In der Praxis funktioniert es meist fünf bis dreißig Minuten nach dem Speichern.",
    textoOtro:
      "Ihr Panel veröffentlicht die Änderung innerhalb weniger Minuten. Zeit braucht nur der Rest des Internets, um davon zu erfahren: Jeder Rechner behält eine Kopie des Notizbuchs eine Weile, um nicht tausendmal nachfragen zu müssen.",
    plazoNormalOtro:
      "Meist funktioniert es zehn Minuten bis eine Stunde nach dem Speichern. Manche Unternehmen brauchen länger, um die Änderung zu veröffentlichen: Rechnen Sie mit bis zu 24 Stunden, bevor Sie sich Sorgen machen.",
    plazoMalo:
      "Wenn Ihre Domain bereits eine andere Website gezeigt hat, kann Ihr eigener Rechner länger brauchen, um das Gespeicherte loszulassen. Das ist kein Fehler. Warten Sie 24 Stunden, bevor Sie sich Sorgen machen.",
    truco:
      "Ein Trick, damit Sie beim Prüfen nicht verzweifeln: Öffnen Sie die Seite in einem privaten Fenster oder auf dem Handy über mobile Daten statt über WLAN. So sehen Sie, was ein neuer Besucher sieht, und nicht das, was Ihr Rechner gespeichert hat.",
    dominioNuevo:
      "Noch etwas: Wenn Sie die Domain gerade erst zu Cloudflare hinzugefügt haben, braucht Cloudflare bis zu 24 Stunden, um sie als aktiv zu markieren. Bis dahin verhält sich alles so, als wären die Wolken grau, auch wenn Sie sie orange sehen. Warten Sie, bis die Domain „Active“ anzeigt, bevor Sie einen Fehler vermuten.",
    ssl:
      "Wenn die Prüfung erfolgreich ist, stellen wir das Sicherheitsschloss aus. Das dauert noch ein paar Minuten. Bis dahin warnt der Browser womöglich, dass die Website nicht sicher ist: Das ist normal und klärt sich von allein.",
  },

  // ── Paso 3: comprobar ──────────────────────────────────────────────────────
  paso3: {
    titulo: "Prüfen wir es",
    descripcion:
      "Wir sehen uns Ihre Domain an und sagen Ihnen, was wir finden. Wenn etwas fehlt, sagen wir Ihnen was und wie Sie es beheben.",
    boton: "Jetzt prüfen",
    botonReintentar: "Erneut prüfen",
    comprobando: "Ihre Einträge werden geprüft…",
    comprobandoAyuda: "Das dauert ein paar Sekunden.",

    resultado: {
      registroOk: "Der DNS-Eintrag ist vorhanden und zeigt auf uns",
      registroKo: "Wir sehen den Eintrag noch nicht",
      proxyOk: "Die Wolke ist grau, genau wie es sein soll",
      proxyKo: "Die Wolke ist orange",
      sslOk: "Sicherheitsschloss ausgestellt",
      sslPendiente: "Sicherheitsschloss wird ausgestellt…",
      webOk: "Ihre Website öffnet sich unter {dominio}, mit Schloss",
      webActivando: "Wir aktivieren Ihre Website auf Ihrer Domain, mit ihrem Sicherheitsschloss",
      webDespues: "Danach prüfen wir, ob Ihre Website sich öffnet, mit Schloss",
    },

    activando: {
      titulo: "Ihre Domain zeigt richtig. Wir aktivieren Ihre Website",
      texto:
        "Es fehlt nur noch das Sicherheitsschloss, es wird automatisch ausgestellt und dauert meist ein bis zwei Minuten. Ändern Sie nichts in {panel}: Wir prüfen gerade und Sie sehen hier, wenn es fertig ist.",
    },

    exito: {
      titulo: "Fertig, Ihre Website liegt jetzt unter {dominio}",
      texto:
        "Sie können die Adresse im Browser eingeben und sie ansehen. Die alte Adresse {actual} funktioniert weiterhin, damit die bereits geteilten Links nicht kaputtgehen.",
      verWeb: "Meine Website ansehen",
      soloWww: "Deine Website öffnet sich unter der Adresse mit «www». Wenn sie auch ohne «www» funktionieren soll, bitte deinen Domain-Anbieter um eine Weiterleitung auf die Adresse mit «www» oder um einen ALIAS/ANAME-Eintrag auf der Wurzel, falls sein Panel das zulässt.",
      copiarEnlace: "Adresse kopieren",
      siguiente: "Setzen Sie sie jetzt in Ihre Profile bei Booking.com, Airbnb und Instagram sowie in Ihre E-Mail-Signatur.",
    },

    pendiente: {
      titulo: "Noch nicht so weit, und das ist normal",
      texto:
        "Wenn Sie es gerade erst in {panel} gespeichert haben, geben Sie ihm ein paar Minuten und klicken Sie erneut. Sie müssen nicht hierbleiben: Wir schreiben Ihnen eine E-Mail, sobald es bereit ist.",
      avisarme: "E-Mail senden, wenn es bereit ist",
    },
  },

  // ── Estados del dominio en la ficha ────────────────────────────────────────
  estados: {
    sinConectar: "Nicht verbunden",
    pendiente: "Warten auf die Einträge",
    comprobando: "Wird geprüft",
    emitiendoSsl: "Schloss wird ausgestellt",
    activo: "Funktioniert",
    error: "Mit Fehler",
    activoAyuda: "Ihre Website liegt unter {dominio} und die Verbindung ist sicher.",
    activandoAyuda: "Ihre Domain zeigt richtig. In wenigen Minuten öffnet sich Ihre Website unter {dominio}, mit Schloss.",
  },

  // ── Errores ────────────────────────────────────────────────────────────────
  errores: {
    formatoInvalido: {
      titulo: "Das sieht nicht nach einer Domain aus",
      texto: "Schreiben Sie sie so: meinedomain.de. Keine Leerzeichen und sonst nichts.",
    },
    conProtocolo: {
      titulo: "Das „https://“ wird nicht gebraucht",
      texto: "Entfernen Sie es und lassen Sie nur den Namen stehen: {dominio}",
    },
    conRuta: {
      titulo: "Alles nach dem Schrägstrich wird nicht gebraucht",
      texto: "Lassen Sie nur den ersten Teil stehen: {dominio}",
    },
    sinExtension: {
      titulo: "Die Endung fehlt",
      texto: "Eine Domain endet auf .com, .de, .net … Prüfen Sie, ob sie nicht abgeschnitten wurde.",
    },
    dominioNoExiste: {
      titulo: "Diese Domain existiert nicht",
      texto:
        "Unter diesem Namen ist niemand registriert. Prüfen Sie die Schreibweise. Wenn Sie sie gerade erst gekauft haben, warten Sie ein paar Stunden und versuchen Sie es erneut.",
    },
    dominioCaducado: {
      titulo: "Diese Domain scheint abgelaufen zu sein",
      texto:
        "Gehen Sie zu dem Unternehmen, bei dem Sie sie gekauft haben, und prüfen Sie, ob die Verlängerung bezahlt ist. Solange sie abgelaufen ist, kann sie nicht verbunden werden.",
    },
    yaUsadoOtraCuenta: {
      titulo: "Diese Domain ist bereits mit einem anderen Konto verbunden",
      texto:
        "Eine Domain kann immer nur auf eine Stelle zeigen. Wenn {dominio} Ihnen gehört, schreiben Sie uns und wir trennen sie vom anderen Konto, damit Sie sie hier nutzen können.",
      contactar: "An den Support schreiben",
    },
    yaUsadoEstaCuenta: {
      titulo: "Sie haben sie bereits verbunden",
      texto:
        "{dominio} wird auf „{sitio}“ genutzt. Trennen Sie sie dort oder wählen Sie eine andere Adresse für diese Website.",
    },
    registrosNoAparecen: {
      titulo: "Wir sehen auf Ihrer Domain noch nichts",
      texto:
        "Meist ist das eine Frage von Minuten: Sie haben gerade gespeichert und es ist noch nicht überall angekommen. Warten Sie zehn Minuten und klicken Sie erneut auf „Prüfen“.",
      repasa: "Wenn eine halbe Stunde vergangen ist, prüfen Sie drei Dinge in {panel}:",
      repasa1: "Dass Sie unter „Name“ {nombre} geschrieben haben und nicht die ganze Domain.",
      repasa2: "Dass Sie auf „Speichern“ geklickt haben und die Zeile in der Liste erscheint.",
      repasa3: "Dass Sie sie auf {dominio} eingetragen haben und nicht auf einer anderen Domain in Ihrem Konto.",
    },
    proxyActivado: {
      titulo: "Die orange Wolke ist eingeschaltet",
      texto:
        "Wir sehen Ihre Domain, aber sie läuft über den Proxy von Cloudflare und der verbirgt das echte Ziel vor uns. Solange sie orange bleibt, funktionieren weder die Prüfung noch das Sicherheitsschloss.",
      solucion:
        "Gehen Sie zu „DNS“ → „Records“, suchen Sie die Zeile mit dem Namen {nombre}, klicken Sie auf die orange Wolke, bis sie grau wird und „DNS only“ anzeigt, und speichern Sie. Kommen Sie dann zurück und klicken Sie auf „Prüfen“.",
    },
    apuntaOtroSitio: {
      titulo: "Ihre Domain zeigt woandershin",
      texto:
        "Wir haben {encontrado} gefunden und {destino} erwartet. Fast immer ist es ein alter Eintrag der vorherigen Website, der übrig geblieben ist.",
      solucion:
        "Löschen Sie unter „DNS“ → „Records“ die alte Zeile mit dem Namen {nombre} und lassen Sie nur unsere stehen.",
    },
    destinoAntiguo: {
      titulo: "Der Wert, den wir Ihnen vorher gegeben haben, hat nicht funktioniert",
      texto:
        "Ihr Eintrag führt zu {encontrado}, also zu dem, was wir Ihnen gesagt haben, aber diese Adresse funktioniert nicht. Der Fehler lag bei uns, nicht bei Ihnen.",
      solucion: "Bearbeiten Sie in {panel} den Eintrag {nombre} und setzen Sie den Wert auf {destino}. Lassen Sie den TXT so, wie er ist.",
    },
    registroDuplicado: {
      titulo: "Es gibt zwei Einträge für denselben Namen",
      texto:
        "Ein Name kann nicht zu zwei Stellen führen. Löschen Sie den, der nicht unserer ist, und prüfen Sie erneut.",
    },
    error1014: {
      titulo: "Wenn der Browser „Error 1014“ anzeigt",
      texto:
        "Das ist nur eine andere Erscheinungsform desselben alten Problems: die orange Wolke. Machen Sie sie grau („DNS only“) und es verschwindet.",
    },
    sslFallido: {
      titulo: "Wir konnten das Sicherheitsschloss nicht ausstellen",
      texto:
        "Der DNS-Eintrag stimmt, aber das Schloss kommt nicht durch. Meist ist es wieder die orange Wolke oder ein alter Eintrag, der im Weg steht. Versuchen Sie es später erneut; bleibt es dabei, schreiben Sie uns und wir sehen es uns an.",
      reintentar: "Ausstellung erneut versuchen",
    },
    sinPermisos: {
      titulo: "Sie können die Domain nicht ändern",
      texto: "Bitten Sie den Kontoinhaber darum.",
    },
    servidor: {
      titulo: "Wir konnten es gerade nicht prüfen",
      texto: "Auf unserer Seite ist etwas schiefgegangen. Versuchen Sie es in ein paar Minuten erneut.",
    },
    guardar: "Die Domain konnte nicht gespeichert werden. Versuchen Sie es erneut.",
  },

  // ── Desconectar ────────────────────────────────────────────────────────────
  desconectar: {
    boton: "Diese Domain trennen",
    titulo: "{dominio} trennen?",
    texto:
      "Ihre Website wird unter {dominio} nicht mehr angezeigt und ist wieder nur unter {actual} erreichbar. Wer Ihre Domain eingibt, findet nichts, bis Sie sie woandershin zeigen lassen.",
    aviso: "Die Einträge, die Sie in {panel} erstellt haben, bleiben bestehen. Löschen Sie sie selbst, wenn Sie sie nicht mehr möchten.",
    cancelar: "Abbrechen",
    confirmar: "Trennen",
    hecho: "Domain getrennt.",
  },

  // ── Dudas ──────────────────────────────────────────────────────────────────
  dudas: {
    titulo: "Fragen, die immer wieder kommen",

    correoPregunta: "Geht dadurch meine E-Mail-Adresse @{dominio} kaputt?",
    correoTexto:
      "Nein. E-Mails laufen über andere Zeilen des Notizbuchs, die MX-Zeilen, und die fassen wir nicht an. Solange Sie diese nicht löschen, bleibt Ihre E-Mail unverändert.",

    otraWebPregunta: "Ich habe eine andere Website auf dieser Domain. Was passiert mit ihr?",
    otraWebTexto:
      "Sie wird nicht mehr angezeigt: Die Domain wechselt zu dieser hier. Wenn Sie beide behalten möchten, nutzen Sie für die von WACheckin eine eigene Adresse wie buchungen.{dominio}.",

    volverAtrasPregunta: "Kann ich das rückgängig machen?",
    volverAtrasTexto:
      "Ja, jederzeit. Sie machen es dort rückgängig, wo Sie es gemacht haben: Löschen Sie den Eintrag in {panel} und setzen Sie den alten wieder ein. Bis es sichtbar ist, dauert es dieselben paar Minuten.",

    wwwPregunta: "Funktioniert es mit und ohne „www“?",
    wwwTexto:
      "Ja, wenn Sie beide Zeilen hinzufügen, die wir Ihnen geben. Eine führt hin und die andere leitet auf die erste weiter, damit Google nicht zwei identische Websites sieht.",

    costePregunta: "Kostet mich das etwas?",
    costeTexto:
      "Das Verbinden nicht. Die Domain bezahlen Sie weiterhin dort, wo Sie sie gekauft haben, genau wie vorher.",

    quienPregunta: "Ich traue mir das nicht zu.",
    quienTexto:
      "Das ist verständlich, es ist der trockenste Teil des ganzen Vorgangs. Schreiben Sie uns mit Ihrer Domain und wir erledigen es gemeinsam am Telefon in zehn Minuten.",
  },

  // ── Botonera ───────────────────────────────────────────────────────────────
  botones: {
    atras: "Zurück",
    siguiente: "Weiter",
    guardar: "Speichern",
    guardando: "Wird gespeichert…",
    cerrar: "Schließen",
    entendido: "Verstanden",
    abrirCloudflare: "Cloudflare öffnen",
    soporte: "Hilfe von WACheckin erhalten",
    verGuia: "Anleitung erneut ansehen",
  },

  avisos: {
    copiado: "In die Zwischenablage kopiert",
    copiadoError: "Es konnte nicht kopiert werden. Markieren Sie es von Hand.",
    guardado: "Domain gespeichert. Fügen Sie jetzt die Einträge in {panel} hinzu.",
    conectado: "Ihre Website liegt jetzt unter {dominio}!",
    correoEnviado: "Wir haben die Anleitung an Ihre E-Mail-Adresse gesendet.",
  },
};
